// Builds Google-compliant JobPosting structured data (schema.org) for a job.
// Handles both the live-jobs feed shape and the paid db.jobs shape.
const EMPLOYMENT_MAP = {
  "full-time": "FULL_TIME",
  "part-time": "PART_TIME",
  "contract": "CONTRACTOR",
  "contractor": "CONTRACTOR",
  "internship": "INTERN",
  "intern": "INTERN",
  "temporary": "TEMPORARY",
  "freelance": "CONTRACTOR",
};

function employmentType(raw) {
  return EMPLOYMENT_MAP[(raw || "").toLowerCase().trim()] || "FULL_TIME";
}

function addDays(iso, days) {
  const d = iso ? new Date(iso) : new Date();
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

export function buildJobPostingLd(job) {
  if (!job || !job.title) return null;
  const company = job.company || job.company_name || "Undisclosed";
  const remote = job.is_remote ?? job.remote ?? false;
  const employment = job.job_type || job.employment_type;
  const country = job.country || "";
  const location = job.location || "";
  const datePosted = job.published_at || job.created_at || new Date().toISOString();

  const description = (job.description && job.description.trim())
    ? job.description
    : [
        `${job.title} at ${company}.`,
        remote ? "This is a remote role." : (location ? `Location: ${location}.` : ""),
        employment ? `Employment type: ${employment}.` : "",
        (job.tags && job.tags.length) ? `Key skills: ${job.tags.slice(0, 8).join(", ")}.` : "",
        `Apply directly via WorldAIInfo.`,
      ].filter(Boolean).join(" ");

  const ld = {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    title: job.title,
    description,
    datePosted,
    hiringOrganization: {
      "@type": "Organization",
      name: company,
      ...(job.company_logo ? { logo: job.company_logo } : {}),
    },
    employmentType: employmentType(employment),
    directApply: false,
  };

  const validThrough = addDays(datePosted, 60);
  if (validThrough) ld.validThrough = validThrough;
  if (job.id) ld.identifier = { "@type": "PropertyValue", name: company, value: String(job.id) };

  // Location. Remote roles use TELECOMMUTE + applicantLocationRequirements.
  if (country || location) {
    ld.jobLocation = {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        ...(location ? { addressLocality: location } : {}),
        ...(country ? { addressCountry: country } : {}),
      },
    };
  }
  if (remote) {
    ld.jobLocationType = "TELECOMMUTE";
    ld.applicantLocationRequirements = { "@type": "Country", name: country || "Worldwide" };
  }

  // Salary
  const min = Number(job.salary_min) || null;
  const max = Number(job.salary_max) || null;
  if (min || max) {
    ld.baseSalary = {
      "@type": "MonetaryAmount",
      currency: job.salary_currency || "USD",
      value: {
        "@type": "QuantitativeValue",
        ...(min ? { minValue: min } : {}),
        ...(max ? { maxValue: max } : {}),
        unitText: "YEAR",
      },
    };
  }

  return ld;
}
